import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { isEmployeeRole } from "@care-by-ella/contracts";
import {
  createClinicianSession,
  destroyClinicianSession,
  fetchClinicianMe,
  type ClinicianClaims,
} from "./api";

// CAR-36: the clinician JWT now lives in an httpOnly cookie set by the API and
// is never readable from JS — no sessionStorage, no localStorage. Auth state
// is derived by probing the API's `me` endpoint (the browser attaches the
// cookie); the JWT only ever touches JS for the few milliseconds between the
// BankID `complete` step and the `POST /session` exchange, then is discarded.

interface AuthState {
  status: "rehydrating" | "signed_out" | "signed_in";
  claims: ClinicianClaims | null;
  // Exchanges the BankID-issued JWT for the httpOnly session cookie, then
  // drops the JWT. Resolves once the session is established.
  signIn: (jwt: string) => Promise<void>;
  // Clears the cookie server-side and resets local state.
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState<ClinicianClaims | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("rehydrating");

  // On mount, ask the API who we are. A 200 (cookie valid) signs us in; a
  // 401/403 (no/expired cookie) signs us out. Network errors also fall back
  // to signed-out so the login screen is reachable.
  useEffect(() => {
    let cancelled = false;
    fetchClinicianMe()
      .then((me) => {
        if (cancelled) return;
        // TES-281: any staff role may hold an admin session — per-route role
        // gating (what a coach actually sees) lands with the coach admin.
        if (me && isEmployeeRole(me.role)) {
          setClaims(me);
          setStatus("signed_in");
        } else {
          setClaims(null);
          setStatus("signed_out");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setClaims(null);
        setStatus("signed_out");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (jwt: string) => {
    // Hand the JWT to the API, which parks it in an httpOnly cookie. The token
    // is never stored in JS — it goes out of scope the moment this returns.
    await createClinicianSession(jwt);
    const me = await fetchClinicianMe();
    if (!me || !isEmployeeRole(me.role)) {
      throw new Error("Session establishment failed");
    }
    setClaims(me);
    setStatus("signed_in");
  }, []);

  const signOut = useCallback(async () => {
    try {
      await destroyClinicianSession();
    } finally {
      // Reset local state regardless — the cookie clear is best-effort and a
      // subsequent authed request would 401 anyway.
      setClaims(null);
      setStatus("signed_out");
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, claims, signIn, signOut }),
    [status, claims, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// TES-1400 — for a field component that is also rendered outside the
// provider (the admin's node tests): the signed-in state, or null.
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext);
}
